/**
 * @graphql cc
 */
import { useMemo } from 'react';

import { useActiveCommentsQuery } from '@confluence/comments-data/entry-points/useActiveCommentsQuery';
import { useProcessedCommentsDataState } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import { CommentsType } from '@confluence/comments-panel-queries/entry-points/ActiveCommentsQuery';

import { useCommentCountsLegacy } from './useCommentCountsLegacy';

/**
 * @deprecated This type is deprecated and will be removed in a future version.
 * It was used to manage comment counts but is no longer needed as part of modernization.
 */
export type UseCommentCountsData = {
	unresolvedInlineCommentsCount: number;
	footerCommentsCount: number;
};

/**
 * @deprecated This hook is deprecated and will be removed in a future version.
 * It was used to fetch comment counts but is no longer needed as part of modernization.
 */
export const useCommentCounts = (contentId: string): UseCommentCountsData => {
	// Always call both hooks to follow React rules, but skip the queries conditionally
	// Legacy implementation using CommentsSectionQuery
	const legacyResult = useCommentCountsLegacy(contentId);

	// New comments data manager - get processed data from the store
	// Get processed comments data for new comments data manager
	const processedCommentsData = useProcessedCommentsDataState();

	// ActiveCommentsQuery implementation - only used when confluence_comment_use_active_comment_query_exp is enabled
	// but NOT when cc_comments_new_comments_data_manager is enabled
	// Call without type filter to get both inline and footer comments, then filter by location.type
	const { data: allCommentsData } = useActiveCommentsQuery({
		pageId: contentId,
		// Don't pass type to get all comment types (both CommentsType.INLINE and CommentsType.FOOTER)
		skip:
			!contentId ||
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) ||
			!expValEquals('confluence_comment_use_active_comment_query_exp', 'isEnabled', true),
	});

	return useMemo(() => {
		// Priority 1: Use new comments data manager when enabled
		if (
			processedCommentsData.isDataProcessed &&
			expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)
		) {
			const unresolvedInlineCommentsCount = processedCommentsData.openInlineComments.length;
			const footerCommentsCount = processedCommentsData.openFooterComments.length;

			return {
				unresolvedInlineCommentsCount,
				footerCommentsCount,
			};
		}

		// Priority 2: Use ActiveCommentsQuery when confluence_comment_use_active_comment_query_exp is enabled
		if (
			allCommentsData?.comments?.nodes &&
			!expVal('cc_comments_new_comments_data_manager', 'isEnabled', false) &&
			expValEquals('confluence_comment_use_active_comment_query_exp', 'isEnabled', true)
		) {
			// New implementation using ActiveCommentsQuery
			// ActiveCommentsQuery already filters by commentState: UNRESOLVED, so all comments are unresolved
			// Filter comments by location.type to count inline vs footer separately
			const unresolvedInlineCommentsCount = allCommentsData.comments.nodes.filter(
				(node) => node?.location?.type === CommentsType.INLINE,
			).length;
			const footerCommentsCount = allCommentsData.comments.nodes.filter(
				(node) => node?.location?.type === CommentsType.FOOTER,
			).length;

			return {
				unresolvedInlineCommentsCount,
				footerCommentsCount,
			};
		}

		// Priority 3: Legacy implementation using CommentsSectionQuery
		// Return legacy result if available, otherwise return default values
		if (legacyResult) {
			return legacyResult;
		}

		// Fallback when experiment is enabled but data hasn't loaded yet
		return {
			unresolvedInlineCommentsCount: 0,
			footerCommentsCount: 0,
		};
	}, [
		processedCommentsData.isDataProcessed,
		processedCommentsData.openInlineComments.length,
		processedCommentsData.openFooterComments.length,
		allCommentsData?.comments?.nodes,
		legacyResult,
	]);
};
